import { useGetPlaybookQuery } from '@carboncopyco/data';
import {
  ArchiveIcon,
  DownOutlineIcon,
  DuplicateOutlineIcon,
  EditIcon,
  LinkIcon,
  OpenInNew1OutlineIcon,
  ShowOutlineIcon,
  SubdirectoryRightOutlineIcon,
} from '@carboncopyco/icons';
import { PermissionAction, PermissionKey } from '@carboncopyco/types';
import { Button, Dropdown, DropdownItem, Separator } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  useAnalytics,
  usePlaybookAnalytics,
} from '@carboncopyco/utils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef, useState } from 'react';
import { useCopyUrl } from '../../../hooks';
import {
  ArchivePlaybookModal,
  DuplicatePlaybookModal,
} from '../../AssetActions/Playbook';
import { MovePlaybookModal } from '../../AssetActions/Playbook/modals/MovePlaybookModal';
import { UpdatePlaybookModal } from '../../PlaybookModal';
import { PlaybookPreviewTooltip } from '../../Tooltip';
import {
  useDesktopDeeplink,
  usePermissionsContext,
  useSearchParamAction,
} from '../../utils';
import { usePlaybookPageProps } from './PlaybookPageContext';

export const PlaybookPageDropdown = () => {
  const {
    playbookId,
    organizationId,
    goToPlaybookPage,
    workspaceId,
    goToWorkspacePage,
    goToFolderPage,
    desktopDomain,
    goToPlaybookPreview,
  } = usePlaybookPageProps();
  const {
    trackPlaybookClickUpdate,
    trackPlaybookClickArchive,
    trackPlaybookClickDuplicate,
  } = usePlaybookAnalytics('playbook_page');
  const { trackEvent } = useAnalytics();
  const { getPermissions } = usePermissionsContext();
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [moveModal, setMoveModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const { exeDeeplink, shouldShowDeeplink } = useDesktopDeeplink();
  const { data: playbook } = useGetPlaybookQuery({
    id: playbookId,
    workspaceId,
    organizationId,
  });
  const { copyCurrentURL } = useCopyUrl();
  const flags = useFlags();
  const previewRef = useRef<HTMLDivElement>(null);
  const deletePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.deletePlaybookById
  );
  const updatePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.updatePlaybookById
  );
  const duplicatePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.duplicatePlaybook
  );
  const movePlaybookPermission = getPermissions(
    PermissionKey.playbook,
    PermissionAction.movePlaybook
  );

  const playbookFolderId = String(playbook?.folderId);

  useSearchParamAction({
    param: 'update',
    action: () => {
      setShowPlaybookModal(true);
    },
    expectedValue: `${playbookId}`,
  });

  return (
    <>
      <Dropdown
        triggerElement={
          <Button
            link
            blue
            rightIcon={<DownOutlineIcon />}
            label="Actions"
          />
        }
        width={180}
        align="center"
        asChild
      >
        <div ref={previewRef}>
          <DropdownItem
            iconLeft={<ShowOutlineIcon />}
            label="Preview"
            onClick={() => {
              trackEvent(AnalyticsTrackEventName.PLAYBOOK_CLICK_PREVIEW, {
                location: 'playbook_info_page_top_actions',
              });
              goToPlaybookPreview();
            }}
          />
        </div>
        <PlaybookPreviewTooltip
          desktopDomain={desktopDomain}
          previewRef={previewRef}
          location={location}
        />
        {updatePlaybookPermission && (
          <DropdownItem
            iconLeft={<EditIcon size={18} />}
            label="Update"
            iconRight={
              shouldShowDeeplink ? <OpenInNew1OutlineIcon size={15} /> : null
            }
            onClick={() => {
              trackPlaybookClickUpdate({ playbookId });
              exeDeeplink('update', playbookId, () =>
                setShowPlaybookModal(true)
              );
            }}
          />
        )}
        {duplicatePlaybookPermission && (
          <DropdownItem
            iconLeft={<DuplicateOutlineIcon size={18} />}
            label="Duplicate"
            onClick={() => {
              trackPlaybookClickDuplicate({ playbookId });
              setShowDuplicateModal(true);
            }}
          />
        )}
        {movePlaybookPermission && (
          <DropdownItem
            iconLeft={<SubdirectoryRightOutlineIcon size={18} />}
            label="Move to..."
            onClick={() => setMoveModal(true)}
          />
        )}
        {flags.copyUrl && (
          <DropdownItem
            iconLeft={<LinkIcon size={18} />}
            label="Copy page URL"
            onClick={copyCurrentURL}
          />
        )}
        {deletePlaybookPermission && duplicatePlaybookPermission && (
          <Separator horizontal />
        )}
        {deletePlaybookPermission && (
          <DropdownItem
            iconLeft={<ArchiveIcon size={18} />}
            label="Archive"
            onClick={() => {
              trackPlaybookClickArchive({ playbookId });
              setShowArchiveModal(true);
            }}
          />
        )}
      </Dropdown>
      {duplicatePlaybookPermission && (
        <DuplicatePlaybookModal
          workspaceId={workspaceId}
          show={showDuplicateModal}
          onClose={() => setShowDuplicateModal(false)}
          playbookId={playbookId}
          organizationId={organizationId}
          goToDuplicatedPlaybook={goToPlaybookPage}
        />
      )}
      {moveModal && (
        <MovePlaybookModal
          playbook={playbook}
          onClose={() => setMoveModal(false)}
          organizationId={organizationId}
          workspaceId={workspaceId}
        />
      )}
      {showArchiveModal && (
        <ArchivePlaybookModal
          workspaceId={workspaceId}
          folderId={playbookFolderId}
          playbookId={playbookId}
          show={showArchiveModal}
          onClose={() => setShowArchiveModal(false)}
          organizationId={organizationId}
          navigateOnSuccess={() =>
            playbookFolderId === '0'
              ? goToWorkspacePage(workspaceId)
              : goToFolderPage(playbookFolderId, workspaceId)
          }
        />
      )}
      {showPlaybookModal && (
        <UpdatePlaybookModal
          onClose={() => setShowPlaybookModal(false)}
          id={playbookId}
        />
      )}
    </>
  );
};
